<template>
  <DataVisGraph
    :key="chartComponentKey"
    :show-grid="true"
    :chart-type="chartType"
    :data-series="statisticsData"
    :loading="$apollo.queries.statisticsData.loading"
    :title="$t(title)"
    :local-date-range="DateRange"
    :local-active-filters="activeFilters"
    :unit="unit"
    @refetch="$apollo.queries.statisticsData.refetch()"
    @remove-filter="removeFilter"
    @updated-date-range="DateRange = $event"
  >
    <template #tag>
      <div class="iblock m-l-5" style="vertical-align: text-bottom">
        <span v-if="highlightContent" class="label label-primary">
          {{ highlightContent }}
        </span>
      </div>
    </template>
    <template #help-slot>
      {{ helpText }}
    </template>
  </DataVisGraph>
</template>

<script>
import gql from 'graphql-tag'
import DateRange from '@/vue-prop-types/DateRange'
import DataVisGraph from '@/components/DataVisGraph.vue'
import { StatisticSeries } from '@/vue-prop-types/Statistics'
import { clean } from '@/utils/tools'
import { PropChartType } from '@/mixins/PropChartType'

export default {
  name: 'DataVisReportCard',
  components: {
    DataVisGraph,
  },
  mixins: [PropChartType],
  props: {
    // chartType // From mixin
    filter: {
      type: Object,
      required: true,
    },
    unit: {
      type: String,
      default: 'currency',
    },
    title: {
      type: String,
      default: 'Untitled',
    },
    chartKey: {
      type: String,
      required: true,
    },
    helpText: {
      type: String,
      default: null,
    },
    dateRange: {
      type: Object,
      default: () => ({ start: null, end: null }),
    },
    // So this might be weird since we have a DateRange, but this simply is a string of type "date - date" which we
    // Then split and interpret as a proper DateRange. This is mainly for forecasting
    initialDateRange: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      statisticsData: new StatisticSeries(),
      dateRangeLocal: null,
    }
  },
  apollo: {
    statisticsData: {
      query: gql`
        query statisticsData(
          $chartKey: String
          $filter: String
          $dateFrom: Date
          $dateTo: Date
          $currency: Currency
        ) {
          statisticsData(
            specifications: {
              chartKey: $chartKey
              filter: $filter
              currency: $currency
              chunkSize: MONTH
              dateRange: { from: $dateFrom, to: $dateTo }
            }
          ) {
            labels
            dataSets {
              dataPoints {
                startDate
                endDate
                value
              }
              legend
              color
            }
            highlightContent
          }
        }
      `,
      variables() {
        return {
          ...this.filter,
          ...this.localFilter,
          ...{
            filter: JSON.stringify(this.filter.filter),
            chartKey: this.chartKey,
          },
        }
      },
      update: (raw) => new StatisticSeries(raw.statisticsData),
    },
  },
  computed: {
    chartComponentKey() {
      if (this.statisticsData.labels) {
        return `${this.chartKey}_${this.statisticsData.labels.join('')}`
      }

      return this.chartKey
    },
    highlightContent() {
      return this.statisticsData.highlight
    },
    DateRange: {
      get() {
        return this.dateRangeLocal || this.dateRange
      },
      set(range) {
        this.dateRangeLocal = DateRange.copy(range)
      },
    },
    activeFilters() {
      const filter = clean({
        'Date Range': this.dateRangeLocal
          ? { id: 'dateRangeLocal', name: this.dateRangeLocal?.toString() }
          : null,
      })
      return Object.keys(filter).map((k) => ({
        type: k,
        value: filter[k].id || null,
        name: filter[k].name || null,
      }))
    },
    localFilter() {
      const FilterRange = this.dateRangeLocal
        ? DateRange.convertToFormat(this.dateRangeLocal, 'YYYY-MM-DD')
        : null
      return clean({
        dateFrom: FilterRange?.start || null,
        dateTo: FilterRange?.end || null,
      })
    },
  },
  created() {
    if (typeof this.initialDateRange === 'string') {
      const dates = this.initialDateRange.replace(' ', '').split('-')
      this.dateRangeLocal = DateRange.fromDateRange(dates[0], dates[1])
    }
  },
  methods: {
    removeFilter(filter) {
      if (filter.type === 'Date Range') {
        this.dateRangeLocal = null
      }
    },
  },
}
</script>

<style scoped></style>
